import { QUESTION_STEPS } from '@/components/OnboardingQuestionPage'
import ContinueButton from '@app-studio-web/ui/components/ContinueButton'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, useLocation, useNavigate } from 'react-router-dom'

const TOOLS = [
  'ChatGPT',
  'Claude',
  'Gemini',
  'Microsoft Copilot',
  'Perplexity',
  'Jasper',
  'Copy.ai',
  'Midjourney',
  'Canva AI',
  'HubSpot AI',
  'Synthesia',
  'None of these',
]

const FREQUENCIES = ['Daily', 'Weekly', 'Monthly']

type ToolSelection = { frequency: string }

export default function AiToolsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const [selections, setSelections] = useState<Record<string, ToolSelection>>(
    {},
  )

  const currentPath = location.pathname.split('/').pop() || ''
  const currentIndex = QUESTION_STEPS.indexOf(currentPath)
  const stepNumber = currentIndex + 1
  const totalSteps = QUESTION_STEPS.length
  const progress = totalSteps > 0 ? (stepNumber / totalSteps) * 100 : 0

  const hasSelections = Object.keys(selections).length > 0

  const toggleTool = (tool: string) => {
    setSelections((prev) => {
      if (tool === 'None of these') {
        return prev['None of these']
          ? {}
          : { 'None of these': { frequency: '' } }
      }
      const next = { ...prev }
      delete next['None of these']
      if (next[tool]) {
        delete next[tool]
      } else {
        next[tool] = { frequency: '' }
      }
      return next
    })
  }

  const setFrequency = (tool: string, freq: string) => {
    setSelections((prev) => ({
      ...prev,
      [tool]: { frequency: freq },
    }))
  }

  return (
    <div className="bg-app-bg text-primary flex min-h-dvh flex-col font-sans">
      <div className="bg-accent/30 fixed top-0 left-0 z-10 h-full w-1">
        <div
          className="bg-accent w-full transition-all duration-500"
          style={{ height: `${progress}%` }}
        />
      </div>

      <div className="bg-border h-1 w-full">
        <div
          className="bg-accent h-full transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="mx-auto flex w-full max-w-2xl items-center justify-between px-6 py-4">
        <button
          onClick={() => navigate(-1)}
          className="text-muted hover:text-primary flex cursor-pointer items-center gap-1 bg-transparent text-sm transition-colors"
        >
          ← {t('shared.back', { defaultValue: 'Back' })}
        </button>
        <span className="text-muted text-sm">
          {stepNumber} / {totalSteps}
        </span>
      </div>

      <div className="mx-auto w-full max-w-2xl grow px-6 pt-10 pb-28">
        <span className="text-accent text-xs font-semibold tracking-widest uppercase">
          STEP {stepNumber}
        </span>

        <h1 className="mt-3 font-serif text-[1.75rem] leading-tight font-bold tracking-tight md:text-[2.25rem]">
          {t('pages.onboarding.aiTools.title', {
            defaultValue: 'Which AI tools do you use?',
          })}
        </h1>

        <p className="text-muted mt-4 text-base leading-relaxed">
          {t('pages.onboarding.aiTools.subtitle', {
            defaultValue: 'Select all that apply for work.',
          })}
        </p>

        <Form method="post" className="mt-10">
          <div className="space-y-3">
            {TOOLS.map((tool) => {
              const isSelected = !!selections[tool]
              const isNone = tool === 'None of these'
              return (
                <div
                  key={tool}
                  className={`overflow-hidden rounded-xl border transition-colors ${
                    isSelected
                      ? 'border-accent/50 bg-accent/5'
                      : 'border-border bg-white'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => toggleTool(tool)}
                    className="flex w-full cursor-pointer items-center justify-between bg-transparent px-5 py-4"
                  >
                    <span className="text-primary font-semibold">
                      {tool === 'None of these'
                        ? t('pages.onboarding.aiTools.none', {
                            defaultValue: 'None of these',
                          })
                        : tool}
                    </span>
                    {isSelected && !isNone && (
                      <span className="bg-accent h-3 w-3 rounded-full" />
                    )}
                  </button>

                  {isSelected && !isNone && (
                    <div className="flex gap-2 px-5 pb-4">
                      {FREQUENCIES.map((freq) => {
                        const active = selections[tool]?.frequency === freq
                        return (
                          <button
                            key={freq}
                            type="button"
                            onClick={() => setFrequency(tool, freq)}
                            className={`cursor-pointer rounded-lg border px-4 py-1.5 text-sm font-medium transition-colors ${
                              active
                                ? 'border-accent bg-accent text-white'
                                : 'border-border text-muted hover:border-accent/30 bg-white'
                            }`}
                          >
                            {t(
                              `pages.onboarding.aiTools.${freq.toLowerCase()}`,
                              { defaultValue: freq },
                            )}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          <input
            type="hidden"
            name={t('pages.onboarding.aiTools.title', {
              lng: 'en',
              defaultValue: 'Which AI tools do you use?',
            })}
            value={JSON.stringify(selections)}
          />

          <div
            className={`border-border bg-app-bg fixed bottom-0 left-0 z-20 w-full border-t px-6 py-4 transition-all duration-200 ${
              hasSelections
                ? 'translate-y-0 opacity-100'
                : 'translate-y-full opacity-0'
            }`}
          >
            <div className="mx-auto max-w-2xl">
              <ContinueButton disabled={!hasSelections} />
            </div>
          </div>
        </Form>
      </div>
    </div>
  )
}
